import type { AiAgent, AiProvider } from '../store/aiSettings'

const DEFAULT_MODELS: Record<AiProvider, string> = {
  openai: 'gpt-4o-mini',
  anthropic: 'claude-haiku-4-5-20251001',
  gemini: 'gemini-1.5-flash',
  xai: 'grok-2-latest',
}

export interface ImageInput {
  url: string
  /** Optional mime hint — required for Gemini (which only accepts
   *  base64 + media_type), optional for OpenAI/Anthropic (which fetch
   *  the URL server-side). */
  mime?: string
}

/**
 * Minimal provider-agnostic call. Optional `images` lets callers feed
 * a list of remote image URLs to a vision-capable model — used by AI
 * campaigns to score photos.
 *
 * - OpenAI / xAI: image_url content blocks alongside the text part.
 * - Anthropic: { type: 'image', source: { type: 'url', url } } blocks.
 * - Gemini: inline_data with base64 bytes (we fetch the URL first).
 *
 * Returns plain text; callers parse JSON / markdown as needed.
 */
export async function callAgent(
  agent: AiAgent,
  systemPrompt: string,
  userPayload: string,
  images?: ImageInput[],
): Promise<string> {
  const model = agent.model || DEFAULT_MODELS[agent.provider]
  const imgs = images ?? []
  switch (agent.provider) {
    case 'openai':
      return await callOpenAi(systemPrompt, userPayload, agent.apiKey, 'https://api.openai.com', model, imgs)
    case 'xai':
      return await callOpenAi(systemPrompt, userPayload, agent.apiKey, 'https://api.x.ai', model, imgs)
    case 'anthropic':
      return await callAnthropic(systemPrompt, userPayload, agent.apiKey, model, imgs)
    case 'gemini':
      return await callGemini(systemPrompt, userPayload, agent.apiKey, model, imgs)
  }
}

/** Strip ``` fences from JSON-ish replies so JSON.parse works on the
 *  most common shapes models emit. */
export function parseJsonish<T = unknown>(text: string): T {
  let s = text.trim()
  if (s.startsWith('```')) {
    s = s.replace(/^```(?:json)?\s*/i, '').replace(/```\s*$/, '').trim()
  }
  // Models sometimes wrap with prose — pull the first balanced { ... }.
  if (!s.startsWith('{') && !s.startsWith('[')) {
    const m = s.match(/[{[][\s\S]*[}\]]/)
    if (m) s = m[0]
  }
  return JSON.parse(s) as T
}

async function callOpenAi(
  systemPrompt: string,
  payload: string,
  apiKey: string,
  baseUrl: string,
  model: string,
  images: ImageInput[],
): Promise<string> {
  const userContent: unknown =
    images.length === 0
      ? payload
      : [
          { type: 'text', text: payload },
          ...images.map((i) => ({ type: 'image_url', image_url: { url: i.url } })),
        ]
  const r = await fetch(`${baseUrl}/v1/chat/completions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiKey}` },
    body: JSON.stringify({
      model,
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userContent },
      ],
    }),
  })
  if (!r.ok) throw new Error(`${r.status} ${await r.text()}`)
  const data = (await r.json()) as { choices?: Array<{ message?: { content?: string } }> }
  return data.choices?.[0]?.message?.content ?? ''
}

async function callAnthropic(
  systemPrompt: string,
  payload: string,
  apiKey: string,
  model: string,
  images: ImageInput[],
): Promise<string> {
  const userBlocks: unknown[] = [
    ...images.map((i) => ({
      type: 'image',
      source: { type: 'url', url: i.url },
    })),
    { type: 'text', text: payload },
  ]
  const r = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model,
      max_tokens: 2048,
      system: systemPrompt,
      messages: [
        {
          role: 'user',
          content: images.length === 0 ? payload : userBlocks,
        },
      ],
    }),
  })
  if (!r.ok) throw new Error(`${r.status} ${await r.text()}`)
  const data = (await r.json()) as { content?: Array<{ type?: string; text?: string }> }
  return (data.content ?? []).find((b) => b.type === 'text')?.text ?? ''
}

async function callGemini(
  systemPrompt: string,
  payload: string,
  apiKey: string,
  model: string,
  images: ImageInput[],
): Promise<string> {
  // Gemini accepts inline_data with base64 + mime — fetch each URL and
  // encode. Skip any image whose fetch fails (404 etc.) so one broken
  // CDN ref doesn't kill the whole call.
  const parts: unknown[] = [{ text: payload }]
  for (const img of images) {
    try {
      const r = await fetch(img.url)
      if (!r.ok) continue
      const blob = await r.blob()
      const mime = img.mime || blob.type || 'image/jpeg'
      const b64 = await blobToBase64(blob)
      parts.push({ inline_data: { mime_type: mime, data: b64 } })
    } catch {
      /* skip */
    }
  }
  const url = `https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(model)}:generateContent?key=${encodeURIComponent(apiKey)}`
  const r = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      systemInstruction: { parts: [{ text: systemPrompt }] },
      contents: [{ role: 'user', parts }],
    }),
  })
  if (!r.ok) throw new Error(`${r.status} ${await r.text()}`)
  const data = (await r.json()) as {
    candidates?: Array<{ content?: { parts?: Array<{ text?: string }> } }>
  }
  return data.candidates?.[0]?.content?.parts?.[0]?.text ?? ''
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => {
      const s = String(r.result ?? '')
      // dataURL like "data:image/jpeg;base64,XXXX" — strip the prefix.
      const comma = s.indexOf(',')
      resolve(comma >= 0 ? s.slice(comma + 1) : s)
    }
    r.onerror = () => reject(r.error)
    r.readAsDataURL(blob)
  })
}
